import React from 'react'
import type Phaser from 'phaser'
import { GlobalStyles } from './ui/GlobalStyles.js'
import { A11yAnnouncer } from './ui/A11yAnnouncer.js'
import { useNavigationStore } from './ui/useNavigationStore'
import { useGameStore } from './ui/useGameStore'
import { useProgressionStore } from './ui/useProgressionStore'
import { useAuthStore } from './ui/useAuthStore'
import { GameDataService } from './services/GameDataService'
import { CardProgressionSystem } from './engine/CardProgressionSystem.js'
import { friendsSocket } from './services/FriendsSocketService'
import { AnalyticsService } from './services/AnalyticsService.js'
// Critical-path screens — eagerly loaded so they appear without delay
import { MainMenu } from './ui/MainMenu'
import { HUD } from './ui/HUD'
import { MatchResult } from './ui/MatchResult'
import { ErrorBoundary } from './ui/ErrorBoundary.js'
import { LoadingScreen } from './ui/LoadingScreen.js'
import { OfflineBanner } from './ui/OfflineBanner.js'
// Secondary screens — code-split and loaded on first navigation
const DeckBuilder       = React.lazy(() => import('./ui/DeckBuilder.js'))
const DeckSelector      = React.lazy(() => import('./ui/DeckSelector.js').then(m => ({ default: m.DeckSelector })))
const MapSelect         = React.lazy(() => import('./ui/MapSelect.js').then(m => ({ default: m.MapSelect })))
const MatchmakingScreen = React.lazy(() => import('./ui/MatchmakingScreen.js').then(m => ({ default: m.MatchmakingScreen })))
const ProgressionScreen = React.lazy(() => import('./ui/ProgressionScreen.js').then(m => ({ default: m.ProgressionScreen })))
const InventoryScreen   = React.lazy(() => import('./ui/InventoryScreen.js').then(m => ({ default: m.InventoryScreen })))
const CardDetailScreen  = React.lazy(() => import('./ui/CardDetailScreen.js').then(m => ({ default: m.CardDetailScreen })))
const SettingsScreen    = React.lazy(() => import('./ui/SettingsScreen.js').then(m => ({ default: m.SettingsScreen })))
const LoginScreen       = React.lazy(() => import('./ui/LoginScreen.js').then(m => ({ default: m.LoginScreen })))
const RegisterScreen    = React.lazy(() => import('./ui/RegisterScreen.js').then(m => ({ default: m.RegisterScreen })))
const AuthScreen        = React.lazy(() => import('./ui/AuthScreen.js').then(m => ({ default: m.AuthScreen })))
const LeaderboardScreen = React.lazy(() => import('./ui/LeaderboardScreen.js').then(m => ({ default: m.LeaderboardScreen })))
const TutorialScreen    = React.lazy(() => import('./ui/TutorialScreen.js').then(m => ({ default: m.TutorialScreen })))
const FeedbackScreen    = React.lazy(() => import('./ui/FeedbackScreen.js').then(m => ({ default: m.FeedbackScreen })))
const AdminScreen       = React.lazy(() => import('./ui/AdminScreen.js').then(m => ({ default: m.AdminScreen })))
const ModeSelectScreen  = React.lazy(() => import('./ui/ModeSelectScreen.js').then(m => ({ default: m.ModeSelectScreen })))
const CampaignScreen         = React.lazy(() => import('./ui/CampaignScreen.js').then(m => ({ default: m.CampaignScreen })))
const CoopMatchmakingScreen  = React.lazy(() => import('./ui/CoopMatchmakingScreen.js').then(m => ({ default: m.CoopMatchmakingScreen })))
const FriendsScreen          = React.lazy(() => import('./ui/FriendsScreen.js').then(m => ({ default: m.FriendsScreen })))
const ReplayScreen           = React.lazy(() => import('./ui/ReplayScreen.js').then(m => ({ default: m.ReplayScreen })))
const ProfileScreen          = React.lazy(() => import('./ui/ProfileScreen.js').then(m => ({ default: m.ProfileScreen })))
const SpectatorBrowserScreen = React.lazy(() => import('./ui/SpectatorBrowserScreen.js').then(m => ({ default: m.SpectatorBrowserScreen })))
const SpectatorViewScreen    = React.lazy(() => import('./ui/SpectatorViewScreen.js').then(m => ({ default: m.SpectatorViewScreen })))
const TournamentScreen       = React.lazy(() => import('./ui/TournamentScreen.js').then(m => ({ default: m.TournamentScreen })))
const ClanScreen             = React.lazy(() => import('./ui/ClanScreen.js').then(m => ({ default: m.ClanScreen })))
const SeasonPassFullScreen   = React.lazy(() => import('./ui/SeasonPassFullScreen.js').then(m => ({ default: m.SeasonPassFullScreen })))
const Shop              = React.lazy(() => import('./ui/Shop.js'))
const PackOpenAnimation = React.lazy(() => import('./ui/PackOpenAnimation.js'))
import { useShopStore } from './ui/useShopStore.js'
import { PushPermissionModal } from './ui/PushPermissionModal.js'
import { NotificationService } from './services/NotificationService.js'
import { ToastNotification } from './ui/ToastNotification.js'
import { useToastStore } from './ui/useToastStore.js'
import { ChallengeModal } from './ui/ChallengeModal.js'
import { StreakRewardModal } from './ui/StreakRewardModal.js'
import type { StreakReward } from './ui/useProgressionStore.js'
import { CampaignSystem } from './engine/CampaignSystem.js'
import { SeasonService } from './services/SeasonService.js'
import { useSeasonStore } from './ui/useSeasonStore.js'
import i18n from './i18n.js'
import { QUEST_DEFINITIONS } from './data/questDefinitions.js'
import { S } from './locales/strings.js'
import campaignData from './data/campaign.json'
import type { CampaignMissionDef } from './interfaces/index.js'

const _campaignMissions = campaignData as CampaignMissionDef[]

/** Returns the ISO 8601 week string for today, e.g. "2026-W23". */
function getISOWeekString(): string {
  const now = new Date()
  // Shift to nearest Thursday (ISO week anchor: week belongs to the year of its Thursday)
  const thursday = new Date(now)
  thursday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + 3)
  const year = thursday.getFullYear()
  // Week 1 is the one containing January 4
  const jan4 = new Date(year, 0, 4)
  const week1Thursday = new Date(jan4)
  week1Thursday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + 3)
  const weekNum = Math.round((thursday.getTime() - week1Thursday.getTime()) / 604800000) + 1
  return `${year}-W${weekNum.toString().padStart(2, '0')}`
}

// Ensure game data is available outside Phaser context (for DeckBuilder etc.)
const gds = GameDataService.getInstance()
try { gds.load() } catch { /* already loaded or loading via Phaser */ }

// Detects newly-completed quests by comparing questProgress snapshots.
// WHY module-level (not inside useEffect): subscriptions set up inside useEffect can miss
// rapid state changes that occur before React's async commit phase runs the effect.
// WHY guarded by _questSubInit: Vite HMR re-executes this module in dev on every save,
// which would pile up duplicate subscriptions. The boolean ensures exactly one is created.
let _questSubInit = false
if (!_questSubInit) {
  _questSubInit = true
  useProgressionStore.subscribe(
    (state, prevState) => {
      const addToast = useToastStore.getState().addToast
      for (const q of state.questProgress) {
        if (!q.completed) continue
        const prev = prevState.questProgress.find((p) => p.questId === q.questId)
        if (prev?.completed) continue  // was already completed before this snapshot
        const def = QUEST_DEFINITIONS.find((d) => d.id === q.questId)
        const title = def?.title ?? q.questId
        const isAchievement = def?.type === 'achievement'
        addToast({
          message: i18n.t(isAchievement ? S.TOAST_ACHIEVEMENT_UNLOCKED : S.TOAST_QUEST_COMPLETED, { title }),
          variant: 'achievement',
          icon: isAchievement ? '🏆' : '✅',
          durationMs: 5000,
        })
      }
    }
  )
}

// Module-level constant — stable reference, safe to use in useEffect dependency arrays.
const AUTH_ONLY_SCREENS = new Set(['auth', 'login', 'register'])

export default function App(): React.ReactElement {
  const currentScreen = useNavigationStore((s) => s.currentScreen)
  const navigate = useNavigationStore((s) => s.navigate)
  const selectedMapId = useNavigationStore((s) => s.selectedMapId)
  const selectedDeckId = useNavigationStore((s) => s.selectedDeckId)
  const setGameMode = useNavigationStore((s) => s.setGameMode)
  const setMatchmakingMatchId = useNavigationStore((s) => s.setMatchmakingMatchId)
  const setIsChallengeMatch = useNavigationStore((s) => s.setIsChallengeMatch)
  const editingDeckId = useNavigationStore((s) => s.editingDeckId)
  const gameMode = useNavigationStore((s) => s.gameMode)
  const selectedMissionIndex = useNavigationStore((s) => s.selectedMissionIndex)
  const setEditingDeckId = useNavigationStore((s) => s.setEditingDeckId)
  const matchPhase = useGameStore((s) => s.phase)
  const matchOutcome = useGameStore((s) => s.matchOutcome)
  const isLoading = useGameStore((s) => s.isLoading)
  const loadingProgress = useGameStore((s) => s.loadingProgress)
  const loadingMessage = useGameStore((s) => s.loadingMessage)

  const decks = useProgressionStore((s) => s.decks)
  const saveDeck = useProgressionStore((s) => s.saveDeck)
  const pushPermissionRequested = useProgressionStore((s) => s.pushPermissionRequested)
  const setPushPermissionRequested = useProgressionStore((s) => s.setPushPermissionRequested)
  const tutorialCompleted = useProgressionStore((s) => s.tutorialCompleted)

  const level = useProgressionStore((s) => s.level)

  const isShopOpen = useShopStore((s) => s.isOpen)
  const showPackAnimation = useShopStore((s) => s.showPackAnimation)
  const lastPackResult = useShopStore((s) => s.lastPackResult)
  const closeShop = useShopStore((s) => s.closeShop)
  const closePackAnimation = useShopStore((s) => s.closePackAnimation)

  const authSession = useAuthStore((s) => s.session)

  const checkDailyStreak = useProgressionStore((s) => s.checkDailyStreak)
  const progressionStreakBroken = useProgressionStore((s) => s.streakBroken)
  const [streakReward, setStreakReward] = React.useState<StreakReward | null>(null)
  const [streakBroken, setStreakBroken] = React.useState(false)

  // Auth gate: redirect unauthenticated users to the auth screen.
  React.useEffect(() => {
    if (!authSession && !AUTH_ONLY_SCREENS.has(currentScreen)) {
      navigate('auth')
    }
  }, [authSession, currentScreen, navigate])

  // Tutorial redirect: fires reactively when the user lands on 'main-menu' while
  // the tutorial has not yet been completed (e.g. after first login).
  React.useEffect(() => {
    if (authSession && !tutorialCompleted && currentScreen === 'main-menu') {
      navigate('tutorial')
    }
  }, [currentScreen, authSession, tutorialCompleted, navigate])

  // Reset weekly quests on mount if the ISO week has rolled over
  React.useEffect(() => {
    useProgressionStore.getState().resetWeeklyQuestsIfNeeded(getISOWeekString())
  }, [])

  // Daily login streak — only fires once per day, only when authenticated
  React.useEffect(() => {
    if (!authSession) return
    const reward = checkDailyStreak()
    if (reward) {
      setStreakReward(reward)
      setStreakBroken(progressionStreakBroken)
    }
  // progressionStreakBroken is read at effect time, not as dep — intentional
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession])

  // Track screen navigation in Firebase Analytics
  React.useEffect(() => {
    AnalyticsService.getInstance().logScreenView(currentScreen)
  }, [currentScreen])

  // Season rotation check — fires once on app load
  React.useEffect(() => {
    const season = SeasonService.getInstance()
    if (season.isSeasonOver() && !useSeasonStore.getState().seasonEndRewardClaimed) {
      // Auto-claim end-of-season reward (use bronze as fallback)
      const league = 'bronze'
      useSeasonStore.getState().claimSeasonEndReward(league)
      useSeasonStore.getState().resetForNewSeason()
    }
  }, [])

  // Auto-save progression to server when the tab is hidden (browser close, switch, mobile background)
  React.useEffect(() => {
    const handleHide = () => { void useProgressionStore.getState().saveToServer() }
    document.addEventListener('visibilitychange', handleHide)
    return () => document.removeEventListener('visibilitychange', handleHide)
  }, [])

  // Init friends WebSocket when user is logged in
  React.useEffect(() => {
    if (authSession !== null) {
      friendsSocket.init(
        authSession.token.accessToken,
        authSession.displayName,
        level * 100
      )
    } else {
      friendsSocket.shutdown()
    }
  }, [authSession, level])

  // Incoming friend challenge modal
  const [incomingChallenge, setIncomingChallenge] = React.useState<{
    challengeId: string
    fromName: string
    expiresAt: number
  } | null>(null)

  React.useEffect(() => {
    const handleMsg = (msg: Record<string, unknown>) => {
      if (msg.type === 'CHALLENGE_RECEIVED') {
        setIncomingChallenge({
          challengeId: typeof msg.challengeId === 'string' ? msg.challengeId : '',
          fromName: typeof msg.fromName === 'string' ? msg.fromName : 'Unknown',
          expiresAt: typeof msg.expiresAt === 'number' ? msg.expiresAt : Date.now() + 60_000,
        })
      }
    }
    friendsSocket.addHandler(handleMsg)
    return () => friendsSocket.removeHandler(handleMsg)
  }, [])

  const handleChallengeAccept = (challengeId: string) => {
    friendsSocket.send({ type: 'CHALLENGE_ACCEPT', challengeId })
    setIncomingChallenge(null)
    setIsChallengeMatch(true)
    setGameMode('pvp')
    navigate('matchmaking')
  }

  const handleChallengeDecline = (challengeId: string) => {
    friendsSocket.send({ type: 'CHALLENGE_DECLINE', challengeId })
    setIncomingChallenge(null)
  }

  // Forward pack-scene open requests to Phaser
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ cards: Array<{ cardId: string; rarity: string; isNew: boolean }>; packType: string }>).detail
      // Retrieve the Phaser game instance registered on the window by main.ts
      const phaserGame = (window as Window & { __phaserGame?: Phaser.Game }).__phaserGame
      if (phaserGame) {
        phaserGame.scene.start('PackOpenScene', detail)
      }
    }
    window.addEventListener('ultimaze:open-pack-scene', handler)
    return () => window.removeEventListener('ultimaze:open-pack-scene', handler)
  }, [])

  React.useEffect(() => {
    if (currentScreen === 'game') {
      const ownedCards = useProgressionStore.getState().ownedCards
      const progression = CardProgressionSystem.getInstance()
      // Build tower stat overrides from card progression — passed to MatchEngine via MatchScene
      const towerStatOverrides: Record<string, { dmgMult: number; spdMult: number; rangeMult: number }> = {}
      for (const tower of gds.towers) {
        const owned = ownedCards[tower.id]
        if (owned !== undefined && owned.copies > 0) {
          const eff = progression.getEffectiveTowerStats(tower, owned)
          towerStatOverrides[tower.id] = {
            dmgMult:   tower.stats.dmg   > 0 ? eff.dmg   / tower.stats.dmg   : 1,
            spdMult:   tower.stats.spd   > 0 ? eff.spd   / tower.stats.spd   : 1,
            rangeMult: tower.stats.range > 0 ? eff.range / tower.stats.range : 1,
          }
        }
      }
      // For campaign missions, pass bot personality and tier to MatchScene
      let botPersonality: string | undefined
      let botTier: number | undefined
      if (gameMode === 'campaign' && selectedMissionIndex !== null) {
        const mission = _campaignMissions[selectedMissionIndex]
        if (mission) {
          botPersonality = mission.botPersonality
          botTier = mission.botTier
        }
      }

      const matchDetail = {
        deckId: selectedDeckId,
        mapId: selectedMapId ?? 'map-vulkan',
        towerStatOverrides,
        botPersonality,
        botTier,
      }
      // Store detail before dispatching so MatchScene.create() can pick it up if the
      // event fires before the listener is registered (race condition safety net).
      ;(window as unknown as Record<string, unknown>)['__pendingMatchStart'] = matchDetail
      AnalyticsService.getInstance().logMatchStart({
        mode: '1v1',
        mapId: selectedMapId ?? 'map-vulkan',
        deckId: selectedDeckId,
        gameMode: gameMode as 'bot' | 'pvp' | 'campaign',
      })
      window.dispatchEvent(new CustomEvent('ultimaze:start-match', { detail: matchDetail }))
    }
  }, [currentScreen, selectedDeckId, selectedMapId, gameMode, selectedMissionIndex])

  React.useEffect(() => {
    if (matchPhase === 'ended') {
      navigate('match-result')
    }
  }, [matchPhase, navigate])

  // Campaign completion: evaluate objective and save progress when a campaign match ends
  const remainingMs = useGameStore((s) => s.remainingMs)
  React.useEffect(() => {
    if (matchPhase !== 'ended' || gameMode !== 'campaign' || selectedMissionIndex === null) return
    const mission = _campaignMissions[selectedMissionIndex]
    if (!mission) return
    const cs = CampaignSystem.getInstance()
    const elapsedMs = 900_000 - remainingMs

    let success = false
    switch (mission.objective.type) {
      case 'destroy_base':
        success = matchOutcome === 'win'
        break
      case 'survive':
        // Success if the player survived the required seconds (even if they eventually lost)
        success = elapsedMs >= mission.objective.target * 1000 || matchOutcome === 'win'
        break
      case 'reach_income':
      case 'kill_units':
        // These are tracked in real-time by CampaignSystem via EventBus
        success = cs.isComplete()
        break
    }

    if (success) {
      cs.saveResult(mission.id, mission.stars)
      const addXp = useProgressionStore.getState().addXp
      const addGold = useProgressionStore.getState().addGold
      addXp(mission.reward.xp)
      addGold(mission.reward.gold)
      useToastStore.getState().addToast({
        message: i18n.t(S.CAMPAIGN_COMPLETE_TOAST, { xp: mission.reward.xp, gold: mission.reward.gold }),
        variant: 'success',
        icon: '🏆',
        durationMs: 5000,
      })
    } else {
      useToastStore.getState().addToast({
        message: i18n.t(S.CAMPAIGN_FAIL_TOAST),
        variant: 'error',
        icon: '❌',
        durationMs: 4000,
      })
    }
    cs.reset()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchPhase])

  // Show a victory toast when a match ends in a win.
  // The match-result screen already displays full stats, but a toast provides
  // immediate feedback while the screen transition is still animating.
  const matchOutcomeRef = React.useRef(matchOutcome)
  React.useEffect(() => {
    if (matchOutcome === 'win' && matchOutcomeRef.current !== 'win') {
      useToastStore.getState().addToast({
        message: i18n.t(S.TOAST_MATCH_WIN, { xp: 100 }),
        variant: 'success',
        icon: '🏆',
        durationMs: 4000,
      })
    }
    matchOutcomeRef.current = matchOutcome
  }, [matchOutcome])

  // Close overlays on Escape key (keyboard accessibility)
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isShopOpen) closeShop()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isShopOpen, closeShop])

  // Navigate away from matchmaking if the device goes offline
  React.useEffect(() => {
    const handleOffline = () => {
      if (currentScreen === 'matchmaking' || currentScreen === 'coop-matchmaking') {
        navigate('main-menu')
        useToastStore.getState().addToast({
          message: 'Keine Verbindung — PvP nicht möglich',
          variant: 'error',
          durationMs: 4000,
        })
      }
    }
    window.addEventListener('offline', handleOffline)
    return () => window.removeEventListener('offline', handleOffline)
  }, [currentScreen, navigate])

  // Handle NAVIGATE messages from the service worker (e.g. notification clicks)
  React.useEffect(() => {
    if (!navigator.serviceWorker) return
    const handler = (event: MessageEvent) => {
      if (
        event.data !== null &&
        typeof event.data === 'object' &&
        'type' in event.data &&
        event.data.type === 'NAVIGATE' &&
        'screen' in event.data &&
        typeof event.data.screen === 'string'
      ) {
        navigate(event.data.screen as Parameters<typeof navigate>[0])
      }
    }
    navigator.serviceWorker.addEventListener('message', handler)
    return () => navigator.serviceWorker!.removeEventListener('message', handler)
  // navigate is stable (Zustand selector), no need to re-subscribe on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMatchFound = (_opponentName: string, matchId: string) => {
    setMatchmakingMatchId(matchId)
    navigate('game')
  }

  const handleFallbackToBot = () => {
    setGameMode('bot')
    navigate('game')
  }

  // Find the deck being edited (if any)
  const editingDeck = editingDeckId != null
    ? decks.find((d) => d.id === editingDeckId) ?? null
    : null

  const isInMatch = currentScreen === 'game'

  // MMR proxy: level × 100. A level 5 player has 500 MMR, level 10 → 1000, etc.
  const playerMmr = level * 100

  // Overlay active = main content must not receive pointer events
  const showPushModal =
    currentScreen === 'match-result' &&
    matchOutcome === 'win' &&
    !pushPermissionRequested

  // Accept: PushPermissionModal already calls requestPermissions() internally before
  // invoking onAccept. Here we only schedule the daily reminder and mark the modal
  // as permanently dismissed so it never shows again.
  const handlePushModalAccept = () => {
    setPushPermissionRequested(true)
    NotificationService.getInstance().scheduleDailyPackReminder(Date.now())
      .catch(() => {})
  }

  // Decline: close the modal permanently without requesting OS permission.
  const handlePushModalDismiss = () => {
    setPushPermissionRequested(true)
  }

  // Block the background whenever an overlay is visible on top of it.
  // streakReward must be included: the modal sits outside the screen-container div,
  // but that div's CSS transform (from screen-enter animation, kept via `forwards`)
  // creates a permanent stacking context that can win hit-testing on some browsers.
  const isAnyOverlayActive = isShopOpen || showPackAnimation || showPushModal || streakReward !== null

  return (
    <ErrorBoundary
      name="App"
      fallback={
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: '#08060F',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: 32, textAlign: 'center',
          }}
          role="alert"
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#E53935', fontSize: 22, marginBottom: 16, fontFamily: 'system-ui, sans-serif' }}>
            Kritischer Fehler
          </h2>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#C9A84C', color: '#000', border: 'none',
              borderRadius: 8, padding: '12px 32px', fontSize: 16,
              fontWeight: 'bold', cursor: 'pointer', minHeight: 44,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Neu laden
          </button>
        </div>
      }
    >
      <GlobalStyles />
      <A11yAnnouncer />
      <OfflineBanner />
      {isLoading && (
        <LoadingScreen progress={loadingProgress} message={loadingMessage} />
      )}
      <React.Suspense fallback={<div style={{ width: '100vw', height: '100vh', background: '#08060f' }} />}>
        <ErrorBoundary name="ScreenRouter">
          <div
            key={currentScreen}
            className="screen-enter"
            style={{
              width: '100vw',
              height: '100vh',
              position: 'relative',
              pointerEvents: (isInMatch || isAnyOverlayActive) ? 'none' : 'auto',
            }}
          >
            <ErrorBoundary name="PhaserGame">
              {/* Phaser mounts its canvas into #game-container (in main.ts).
                  This boundary isolates renderer crashes from the React UI. */}
              <div id="phaser-error-boundary-anchor" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
            </ErrorBoundary>
            {currentScreen === 'tutorial' && (
              <ErrorBoundary name="TutorialScreen"><TutorialScreen /></ErrorBoundary>
            )}
            {currentScreen === 'mode-select' && (
              <ErrorBoundary name="ModeSelectScreen"><ModeSelectScreen /></ErrorBoundary>
            )}
            {currentScreen === 'campaign' && (
              <ErrorBoundary name="CampaignScreen"><CampaignScreen /></ErrorBoundary>
            )}
            {currentScreen === 'coop-matchmaking' && (
              <ErrorBoundary name="CoopMatchmakingScreen"><CoopMatchmakingScreen /></ErrorBoundary>
            )}
            {currentScreen === 'friends' && (
              <ErrorBoundary name="FriendsScreen"><FriendsScreen /></ErrorBoundary>
            )}
            {currentScreen === 'replay' && (
              <ErrorBoundary name="ReplayScreen"><ReplayScreen /></ErrorBoundary>
            )}
            {currentScreen === 'profile' && (
              <ErrorBoundary name="ProfileScreen"><ProfileScreen /></ErrorBoundary>
            )}
            {currentScreen === 'feedback' && (
              <ErrorBoundary name="FeedbackScreen"><FeedbackScreen /></ErrorBoundary>
            )}
            {currentScreen === 'admin' && (
              <ErrorBoundary name="AdminScreen"><AdminScreen /></ErrorBoundary>
            )}
            {currentScreen === 'main-menu' && (
              <ErrorBoundary name="MainMenu"><MainMenu /></ErrorBoundary>
            )}
            {currentScreen === 'deck-builder' && (
              <ErrorBoundary name="DeckBuilder">
                <DeckBuilder
                  deck={editingDeck}
                  allTowers={gds.towers}
                  allUnits={gds.units}
                  allHeroes={gds.heroes}
                  onSave={(savedDeck) => {
                    saveDeck(savedDeck)
                    setEditingDeckId(null)
                    navigate('deck-selector')
                  }}
                  onCancel={() => {
                    setEditingDeckId(null)
                    navigate('deck-selector')
                  }}
                />
              </ErrorBoundary>
            )}
            {currentScreen === 'deck-selector' && (
              <ErrorBoundary name="DeckSelector"><DeckSelector /></ErrorBoundary>
            )}
            {currentScreen === 'map-select' && (
              <ErrorBoundary name="MapSelect"><MapSelect maps={gds.maps} /></ErrorBoundary>
            )}
            {currentScreen === 'matchmaking' && (
              <ErrorBoundary name="MatchmakingScreen">
                <MatchmakingScreen
                  mapId={selectedMapId ?? 'map-vulkan'}
                  deckId={selectedDeckId}
                  playerMmr={playerMmr}
                  onMatchFound={handleMatchFound}
                  onFallbackToBot={handleFallbackToBot}
                />
              </ErrorBoundary>
            )}
            {currentScreen === 'game' && (
              <ErrorBoundary name="HUD"><HUD /></ErrorBoundary>
            )}
            {currentScreen === 'match-result' && (
              <ErrorBoundary name="MatchResult">
                <MatchResult onPlayAgain={() => navigate('main-menu')} />
              </ErrorBoundary>
            )}
            {currentScreen === 'progression' && (
              <ErrorBoundary name="ProgressionScreen"><ProgressionScreen /></ErrorBoundary>
            )}
            {currentScreen === 'inventory' && (
              <ErrorBoundary name="InventoryScreen"><InventoryScreen /></ErrorBoundary>
            )}
            {currentScreen === 'card-detail' && (
              <ErrorBoundary name="CardDetailScreen"><CardDetailScreen /></ErrorBoundary>
            )}
            {currentScreen === 'settings' && (
              <ErrorBoundary name="SettingsScreen"><SettingsScreen /></ErrorBoundary>
            )}
            {currentScreen === 'login' && (
              <ErrorBoundary name="LoginScreen"><LoginScreen /></ErrorBoundary>
            )}
            {currentScreen === 'register' && (
              <ErrorBoundary name="RegisterScreen"><RegisterScreen /></ErrorBoundary>
            )}
            {currentScreen === 'auth' && (
              <ErrorBoundary name="AuthScreen"><AuthScreen /></ErrorBoundary>
            )}
            {currentScreen === 'leaderboard' && (
              <ErrorBoundary name="LeaderboardScreen"><LeaderboardScreen /></ErrorBoundary>
            )}
            {currentScreen === 'spectator-browser' && (
              <ErrorBoundary name="SpectatorBrowserScreen"><SpectatorBrowserScreen /></ErrorBoundary>
            )}
            {currentScreen === 'spectator-view' && (
              <ErrorBoundary name="SpectatorViewScreen"><SpectatorViewScreen /></ErrorBoundary>
            )}
            {currentScreen === 'tournament' && (
              <ErrorBoundary name="TournamentScreen"><TournamentScreen /></ErrorBoundary>
            )}
            {currentScreen === 'clan' && (
              <ErrorBoundary name="ClanScreen"><ClanScreen /></ErrorBoundary>
            )}
            {currentScreen === 'season-pass' && (
              <ErrorBoundary name="SeasonPassFullScreen"><SeasonPassFullScreen /></ErrorBoundary>
            )}
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="Shop">
          {isShopOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                pointerEvents: 'all',
                zIndex: 200,
              }}
            >
              <Shop onClose={closeShop} />
            </div>
          )}
        </ErrorBoundary>
        <ErrorBoundary name="PackOpenAnimation">
          {showPackAnimation && lastPackResult && (
            <div style={{ position: 'fixed', inset: 0, pointerEvents: 'all', zIndex: 300 }}>
              <PackOpenAnimation cards={lastPackResult} onClose={closePackAnimation} />
            </div>
          )}
        </ErrorBoundary>
      </React.Suspense>
      {showPushModal && (
        <div style={{ pointerEvents: 'all' }}>
          <PushPermissionModal
            onAccept={handlePushModalAccept}
            onDecline={handlePushModalDismiss}
          />
        </div>
      )}
      {/* Toast overlay — zIndex 9000 keeps it above all other overlays */}
      <ToastNotification />
      {/* Challenge modal — zIndex 3000, above all overlays except toasts */}
      {incomingChallenge !== null && (
        <ChallengeModal
          challengeId={incomingChallenge.challengeId}
          fromName={incomingChallenge.fromName}
          expiresAt={incomingChallenge.expiresAt}
          onAccept={handleChallengeAccept}
          onDecline={handleChallengeDecline}
        />
      )}
      {streakReward !== null && (
        <StreakRewardModal
          reward={streakReward}
          streakBroken={streakBroken}
          onClose={() => setStreakReward(null)}
        />
      )}
    </ErrorBoundary>
  )
}
